/*
auto-generated by: https://github.com/pmndrs/gltfjsx
*/
import React, { useRef, useState, useEffect, Suspense, useMemo } from "react";
import { useFrame } from "react-three-fiber";
import { useGLTF } from "@react-three/drei/useGLTF";
import * as THREE from "three";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import { DoubleSide, Object3D, Vector3 } from "three";
import { OrthographicCamera, PerspectiveCamera } from "@react-three/drei";
import { dtr } from "./HelperFunctions";

type GLTFResult = GLTF & {
  nodes: {
    headx_1: THREE.Mesh;
    headx_2: THREE.Mesh;
    headx_3: THREE.Mesh;
    headx_4: THREE.Mesh;
    headx_5: THREE.Mesh;
  };
  materials: {
    metalSkin: THREE.MeshStandardMaterial;
    metalGray: THREE.MeshStandardMaterial;
    insideFace: THREE.MeshStandardMaterial;
    metalEyes: THREE.MeshStandardMaterial;
    pupils: THREE.MeshStandardMaterial;
  };
};

type ActionName = "KeyAction";
type GLTFActions = Record<ActionName, THREE.AnimationAction>;

function Loading() {
  const loadingGeom = useMemo(
    () => new THREE.SphereBufferGeometry(1, 16, 16),
    []
  );
  const loadingMat = useMemo(() => new THREE.MeshStandardMaterial(), []);

  return (
    <mesh
      position={[0, 0, 0]}
      rotation={[0, 0, 0]}
      geometry={loadingGeom}
      material={loadingMat}
    ></mesh>
  );
}

var theta = 0;
var direction = 1;

export default function Model(props: any) {
  const group = useRef<THREE.Group>();
  const groupMesh = useRef<THREE.Group>();
  const { nodes, materials, animations } = useGLTF("/4sides.glb") as GLTFResult;
  const [mixer] = useState(() => new THREE.AnimationMixer(null as any));

  // const [open, setOpen] = useState(false);
  const [cooldown, setCooldown] = useState(false);

  const cam = useRef<THREE.PerspectiveCamera>();

  // materials.insideFace.color = new THREE.Color("#a82");
  materials.insideFace.color = new THREE.Color("#5D5B8A");

  materials.metalSkin.color = new THREE.Color("#787272");
  materials.metalGray.color = new THREE.Color("#222222");

  materials.metalEyes.color = new THREE.Color("#a82");
  materials.metalEyes.metalness = 0.9;

  materials.pupils.color = new THREE.Color("#787272");
  materials.pupils.metalness = 1;

  // if(group.current !== undefined && group.current !== null){
  //   console.log("stopping animations");
  //   void mixer.clipAction(animations[0], group.current).stop();
  //   void mixer.clipAction(animations[1], group.current).stop();
  // }

  useFrame((state, delta) => {
    mixer.update(delta);
    increaseTheta();

    if (cam.current !== undefined) {
      cam.current.lookAt(0, 0, 0);

      cam.current.position.x = Math.cos(theta);
      cam.current.position.y = Math.sin(theta);

      if (modelOpen) {
        if (cam.current.position.z < 9) {
          cam.current.position.z = cam.current.position.z + 0.005;
        }
      } else {
        if (cam.current.position.z > 7) {
          cam.current.position.z = cam.current.position.z - 0.005;
        }
      }
    }
  });

  useEffect(() => {
    if (group.current !== undefined && group.current !== null) {
      // console.log("use effect model")
      mixer.clipAction(animations[1], group.current).reset();

      mixer.clipAction(animations[1], group.current).weight = 1;
      mixer.clipAction(animations[0], group.current).weight = 0;

      void mixer.clipAction(animations[1], group.current).play();
      mixer.clipAction(animations[1], group.current).paused = true;

      group.current.updateMatrix();
    }
  }, [props.tabIsTransitioning]);

  const increaseTheta = () => {
    if (direction === 1) {
      if (theta < 6.28) {
        theta = theta + 0.005;
      } else {
        direction = -1;
      }
    } else {
      if (theta > -6.28) {
        theta = theta - 0.005;
      } else {
        direction = 1;
      }
    }
    // console.log("theta:", theta)
  };

  const [modelOpen, setModelOpen] = useState(false);

  const switchOpenClose = () => {
    setModelOpen(!modelOpen);
  };

  const playAnimOpen = () => {
    if (cooldown === false) {
      if (modelOpen === false) {
        void mixer.clipAction(animations[1], group.current).reset();
        mixer.clipAction(animations[1], group.current).clampWhenFinished = true;
        mixer.clipAction(animations[1], group.current).loop = THREE.LoopOnce;

        mixer.clipAction(animations[1], group.current).weight = 1;
        mixer.clipAction(animations[0], group.current).weight = 0;

        void mixer.clipAction(animations[1], group.current).play();
      } else {
        void mixer.clipAction(animations[0], group.current).reset();
        mixer.clipAction(animations[0], group.current).clampWhenFinished = true;
        mixer.clipAction(animations[0], group.current).loop = THREE.LoopOnce;

        mixer.clipAction(animations[1], group.current).weight = 0;
        mixer.clipAction(animations[0], group.current).weight = 1;

        void mixer.clipAction(animations[0], group.current).play();
      }

      // props.setModelOpen();
      // setCooldown(true);

      switchOpenClose();
      setCooldown(true);

      setTimeout(() => setCooldown(false), 2200);
    }
  };

  return (
    <Suspense fallback={<Loading />}>
      <group ref={group} {...props} dispose={null} onClick={playAnimOpen}>
        <PerspectiveCamera
          ref={cam}
          zoom={3}
          position={[0, 0, 10]}
          rotation={[dtr(0), 0, 0]}
          makeDefault
        ></PerspectiveCamera>

        <group position={[0, 0.5, 0]} ref={groupMesh}>
          <mesh
            material={materials.metalSkin}
            geometry={nodes.headx_1.geometry}
            name="headx_1"
            morphTargetDictionary={nodes.headx_1.morphTargetDictionary}
            morphTargetInfluences={nodes.headx_1.morphTargetInfluences}
            castShadow
            receiveShadow
          />
          <mesh
            material={materials.metalGray}
            geometry={nodes.headx_2.geometry}
            name="headx_2"
            morphTargetDictionary={nodes.headx_2.morphTargetDictionary}
            morphTargetInfluences={nodes.headx_2.morphTargetInfluences}
            castShadow
            receiveShadow
          />
          <mesh
            material={materials.insideFace}
            geometry={nodes.headx_3.geometry}
            name="headx_3"
            morphTargetDictionary={nodes.headx_3.morphTargetDictionary}
            morphTargetInfluences={nodes.headx_3.morphTargetInfluences}
            castShadow
            receiveShadow
          />
          <mesh
            material={materials.metalEyes}
            geometry={nodes.headx_4.geometry}
            name="headx_4"
            morphTargetDictionary={nodes.headx_4.morphTargetDictionary}
            morphTargetInfluences={nodes.headx_4.morphTargetInfluences}
            castShadow
            receiveShadow
          />
          <mesh
            material={materials.pupils}
            geometry={nodes.headx_5.geometry}
            name="headx_5"
            morphTargetDictionary={nodes.headx_5.morphTargetDictionary}
            morphTargetInfluences={nodes.headx_5.morphTargetInfluences}
            castShadow
            receiveShadow
          />
        </group>
      </group>
    </Suspense>
  );
}

useGLTF.preload("/4sides.glb");
